import { TreeObject } from '@models';
import { LocalTreeBuildResult, LocalTreeNode } from './local-tree-types';

export function buildLocalTree<OPTION extends TreeObject>(
  items: OPTION[]
): LocalTreeBuildResult<OPTION> {
  const nodeMap = new Map<string, LocalTreeNode<OPTION>>();
  const roots: LocalTreeNode<OPTION>[] = [];

  items.forEach((item) => {
    nodeMap.set(item.id, {
      id: item.id,
      parentId: item.parent?.id ?? null,
      value: item,
      children: [],
    });
  });

  items.forEach((item) => {
    const node = nodeMap.get(item.id)!;
    const parentId = item.parent?.id;

    if (parentId) {
      const parentNode = nodeMap.get(parentId);

      if (parentNode) {
        parentNode.children.push(node);
        return;
      }
    }

    roots.push(node);
  });

  return {
    roots,
    nodeMap,
  };
}

export function getAncestorIds<OPTION extends TreeObject>(
  nodeId: string,
  nodeMap: Map<string, LocalTreeNode<OPTION>>
): string[] {
  const ancestorIds: string[] = [];
  let currentNode = nodeMap.get(nodeId);

  while (currentNode?.parentId) {
    ancestorIds.unshift(currentNode.parentId);
    currentNode = nodeMap.get(currentNode.parentId);
  }

  return ancestorIds;
}

export function getAncestorLabels<OPTION extends TreeObject>(
  item: OPTION,
  nodeMap: Map<string, LocalTreeNode<OPTION>>
): string[] {
  const node = nodeMap.get(item.id);

  if (node) {
    const ancestorLabels: string[] = [];
    let currentNode: LocalTreeNode<OPTION> | undefined = node;

    while (currentNode?.parentId) {
      currentNode = nodeMap.get(currentNode.parentId);

      if (currentNode) {
        ancestorLabels.unshift(currentNode.value.label);
      }
    }

    return ancestorLabels;
  }

  const ancestorLabels: string[] = [];
  let parent = item.parent;

  while (parent) {
    ancestorLabels.unshift(parent.label);
    parent = parent.parent;
  }

  return ancestorLabels;
}

export function formatSelectedLocalTreeAncestors<OPTION extends TreeObject>(
  item: OPTION,
  nodeMap: Map<string, LocalTreeNode<OPTION>>
) {
  const ancestorLabels = getAncestorLabels(item, nodeMap);

  if (ancestorLabels.length === 0) {
    return '';
  }

  return `(${ancestorLabels.join(' / ')})`;
}

export function getVisibleTree<OPTION extends TreeObject>({
  roots,
  nodeMap,
  expandedIds,
  searchQuery,
}: {
  roots: LocalTreeNode<OPTION>[];
  nodeMap: Map<string, LocalTreeNode<OPTION>>;
  expandedIds: Set<string>;
  searchQuery: string;
}): LocalTreeNode<OPTION>[] {
  if (!searchQuery.trim()) {
    return roots
      .map((node) => cloneVisibleNode(node, expandedIds))
      .filter((node): node is LocalTreeNode<OPTION> => node !== null);
  }

  const normalizedQuery = normalizeValue(searchQuery);
  const matchedIds = new Set<string>();
  const essentialIds = new Set<string>();

  nodeMap.forEach((node) => {
    if (normalizeValue(node.value.label).includes(normalizedQuery)) {
      matchedIds.add(node.id);
      essentialIds.add(node.id);

      getAncestorIds(node.id, nodeMap).forEach((ancestorId) => {
        essentialIds.add(ancestorId);
      });
    }
  });

  if (matchedIds.size === 0) {
    return [];
  }

  return roots
    .map((node) =>
      cloneFilteredNode(node, essentialIds, matchedIds, expandedIds)
    )
    .filter((node): node is LocalTreeNode<OPTION> => node !== null);
}

function cloneVisibleNode<OPTION extends TreeObject>(
  node: LocalTreeNode<OPTION>,
  expandedIds: Set<string>
): LocalTreeNode<OPTION> | null {
  return {
    ...node,
    children: expandedIds.has(node.id)
      ? node.children
          .map((childNode) => cloneVisibleNode(childNode, expandedIds))
          .filter(
            (childNode): childNode is LocalTreeNode<OPTION> =>
              childNode !== null
          )
      : [],
  };
}

function cloneFilteredNode<OPTION extends TreeObject>(
  node: LocalTreeNode<OPTION>,
  essentialIds: Set<string>,
  matchedIds: Set<string>,
  expandedIds: Set<string>,
  forceInclude = false
): LocalTreeNode<OPTION> | null {
  if (!forceInclude && !essentialIds.has(node.id)) {
    return null;
  }

  const revealAllChildren =
    expandedIds.has(node.id) && (forceInclude || matchedIds.has(node.id));

  return {
    ...node,
    children: node.children
      .map((childNode) =>
        cloneFilteredNode(
          childNode,
          essentialIds,
          matchedIds,
          expandedIds,
          revealAllChildren
        )
      )
      .filter(
        (childNode): childNode is LocalTreeNode<OPTION> => childNode !== null
      ),
  };
}

function normalizeValue(value: string) {
  return value.trim().toLocaleLowerCase();
}
